"use client";

import { Card } from "@mui/material";
import React, { useEffect, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import Suspense from "@/components/molecules/Suspense";
import { useNotification } from "@/contexts/notification-context";
import { uniqueMessagesById } from "@/helpers/date-helpers";
import useLoading from "@/hooks/use-loading";
import { IMessage } from "@/interfaces/message.interface";
import IUser from "@/interfaces/user.interface";
import { CurrentChatMessages } from "@/messages/chat/current-chat.messages";
import conversationService from "@/services/conversation.service";
import { PaginatedResponse } from "@/types/requests.types";

import LoadingContainer from "../ChatLoading";
import CurrentChatContent from "../CurrentChatContent";
import CurrentChatHeader from "../CurrentChatHeader";
import { FilePreview } from "../FilePreview";

type CurrentChatProps = {
  messages: CurrentChatMessages;
  user?: IUser | null;
  contactId: number;
  contactName: string;
  conversationMessages?: PaginatedResponse<IMessage> | null;
};

export default function ChatHistory({
  messages,
  contactId,
  contactName,
  conversationMessages,
}: CurrentChatProps) {
  const [chatMessages, setChatMessages] = useState<PaginatedResponse<IMessage>>({
    items: [],
    totalCount: 0,
    pageNumber: 0,
    pageSize: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { activeChatPlatformId } = useNotification();
  const platformId = activeChatPlatformId;

  const {
    startLoading,
    endLoading,
    isLoading: isLoadingMoreMessages,
    loadingIcon: loadingIconMoreMessages,
  } = useLoading({ loadingIconColor: "primary" });

  useEffect(() => {
    const loadInitialChatMessages = async () => {
      if (conversationMessages) {
        setChatMessages(conversationMessages);
        return;
      }
      if (typeof platformId !== "number") {
        return;
      }

      const response = await conversationService.getConversationDetails(
        { contactId, platformId },
        { pageNumber: 1, pageSize: 5 }
      );

      setChatMessages(response);
    };

    if (!platformId) return;
    loadInitialChatMessages();
  }, [contactId, platformId, conversationMessages]);

  const { loadMoreMessages } = messages;

  const next = async () => {
    if (typeof platformId !== "number") {
      return;
    }

    startLoading();

    const pageNumber = chatMessages.pageNumber! + 1;
    const pageSize = chatMessages.pageSize;
    const response = await conversationService.getConversationDetails(
      { contactId, platformId: platformId },
      { pageNumber, pageSize }
    );

    setChatMessages((prev) => {
      return {
        ...response,
        items: uniqueMessagesById([...response.items, ...prev.items]),
      };
    });

    endLoading();
  };

  const currentChatMessages = chatMessages.items || [];
  const totalMessages = chatMessages.totalCount || 0;
  const loadedMessages = chatMessages.items.length || 0;

  return (
    <Card sx={{ minHeight: "70vh" }}>
      <Suspense fallback={<LoadingContainer />}>
        <CurrentChatHeader contactName={contactName} />

        {loadedMessages < totalMessages ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" mt={1}>
            <MDButton variant="contained" color="primary" onClick={next}>
              {isLoadingMoreMessages ? loadingIconMoreMessages : loadMoreMessages}
            </MDButton>
          </MDBox>
        ) : null}

        {currentChatMessages.length > 0 && (
          <CurrentChatContent
            chatMessages={currentChatMessages}
            messages={messages}
            selectedChat={null}
            lastPageLoadTime={null}
          />
        )}

        {selectedFile && (
          <FilePreview
            file={selectedFile}
            onSelectFile={() => setSelectedFile(null)}
            messages={messages}
          />
        )}
      </Suspense>
    </Card>
  );
}
